import cloneDeep from 'lodash/cloneDeep.js';
import { noop } from './utils.js';
import { warn } from '../../utils/console.js';

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }
  if (!value || !value.then) {
    value = Promise.resolve(value);
  }
  return then ? value.then(then) : value;
}
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
var script = {
  name: 'PendoForm',
  provide() {
    return {
      $form: this
    };
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    model: {
      type: Object,
      default: () => ({})
    },
    rules: {
      type: Object,
      default: () => ({})
    },
    labelPosition: {
      type: String,
      default: 'top',
      validator: labelPosition => ['top', 'left', 'right'].includes(labelPosition)
    },
    inline: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    validateOnRuleChange: {
      type: Boolean,
      default: true
    },
    callValidate: {
      type: Boolean,
      default: false
    },
    callResetFields: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      fields: [],
      watchers: [],
      errors: {}
    };
  },
  watch: {
    rules() {
      if (this.validateOnRuleChange) {
        this.validate(noop);
      }
    },
    errors: {
      handler(val) {
        const errors = Object.values(val).includes(true);
        this.$emit('input', !errors);
      },
      deep: true,
      immediate: true
    }
  },
  updated() {
    this.$nextTick(function () {
      if (this.callValidate) {
        warn("'callValidate' is deprecated. Use a ref and call the public 'validate' function instead", this);
        this.validateForm(true);
      }
      if (this.callResetFields) {
        warn("'callResetFields' is deprecated. Use a ref and call the public 'reset' function instead", this);
        this.resetFields(true);
      }
    });
  },
  mounted: _async(function () {
    const _this = this;
    return _await(_this.$nextTick(), function () {
      _this.validate(noop, false);
    });
  }),
  methods: {
    register(field) {
      if (field) {
        this.fields.push(field);
        this.watchers.push(this.watchField(field));
      }
    },
    unregister(fieldToUnregister) {
      const found = this.fields.find(field => field._uid === fieldToUnregister._uid);
      if (!found) {
        return;
      }
      const unwatch = this.watchers.find(watcher => watcher._uid === found._uid);
      if (unwatch) {
        unwatch.valid();
      }
      this.fields = this.fields.filter(field => field._uid !== found._uid);
      this.watchers = this.watchers.filter(watcher => watcher._uid !== found._uid);
      this.$delete(this.errors, found._uid);
    },
    watchField(input) {
      const watcher = input => {
        return input.$watch('invalid', val => {
          this.$set(this.errors, input._uid, val);
        }, {
          immediate: true
        });
      };
      const watchers = {
        _uid: input._uid,
        valid: watcher(input)
      };
      return watchers;
    },
    reset: _async(function () {
      const _this2 = this;
      if (!_this2.model) {
        return;
      }
      _this2.fields.forEach(field => {
        field.reset();
      });
      return _await(_this2.$nextTick(), function () {
        _this2.validate(noop, false);
      });
    }),
    resetValidation(props) {
      let fieldsToReset = this.fields;
      if (props) {
        const propsToReset = Array.isArray(props) ? props : [props];
        fieldsToReset = this.fields.filter(field => propsToReset.includes(field.prop));
      }
      fieldsToReset.forEach(field => {
        field.resetValidation();
      });
    },
    validate(callback) {
      let forceDirty = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      if (!this.model) {
        return;
      }
      let promise;
      if (typeof callback !== 'function' && window.Promise) {
        promise = new window.Promise((resolve, reject) => {
          callback = function (valid, invalidFields) {
            if (valid) {
              resolve(valid);
            } else {
              reject(invalidFields);
            }
          };
        });
      }
      if (this.fields.length === 0 && callback) {
        callback(true);
      }
      let valid = true;
      let count = 0;
      let invalidFields = {};
      this.fields.forEach(field => {
        if (forceDirty) {
          field.touch();
        }
        field.validate('', (message, field) => {
          if (message) {
            valid = false;
          }
          invalidFields = Object.assign({}, invalidFields, field);
          if (typeof callback === 'function' && ++count === this.fields.length) {
            callback(valid, invalidFields);
          }
        });
      });
      if (promise) {
        return promise;
      }
    },
    validateField(props, callback) {
      const propsToValidate = Array.isArray(props) ? props : [props];
      const fieldsToValidate = this.fields.filter(field => propsToValidate.includes(field.prop));
      if (!fieldsToValidate.length) {
        return;
      }
      fieldsToValidate.forEach(field => {
        field.validate('', callback);
      });
    },
    resetFields(calledFromUpdatedHook) {
      if (!calledFromUpdatedHook) {
        warn("'resetFields' is deprecated. Use a ref and call the public 'reset' function instead", this);
      }
      this.reset();
      this.$emit('fieldsReset');
    },
    validateForm(calledFromUpdatedHook) {
      if (!calledFromUpdatedHook) {
        warn("'validateForm' is deprecated. Use a ref and call the public 'validate' function instead", this);
      }
      this.validate(valid => {
        if (valid) {
          this.$emit('formValidated', cloneDeep(this.model));
        } else {
          this.$emit('invalidForm');
        }
      });
    },
    onSubmit($event) {
      this.$emit('submit', $event);
      this.validateForm();
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-form.vue2.js.map
